import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';
import Sortable, { Options, SortableEvent } from 'sortablejs';

/**
 * Drag-and-drop reordering, replacing ngx-sortablejs.
 *
 * That package was last published for Angular 11 and ships ViewEngine metadata, which Angular 16
 * can no longer consume at all -- ngcc, the compatibility compiler that used to translate those
 * packages, was removed. It was a thin wrapper over sortablejs, which this project already depends
 * on directly, so the wrapper is the only part worth replacing.
 *
 * The selector and inputs deliberately match the old ones ([sortablejs] / [sortablejsOptions]) so
 * templates did not have to change.
 */
@Directive({
    selector: '[sortablejs]',
    standalone: false
})
export class SortableDirective implements AfterViewInit, OnDestroy {
    /** The array being reordered. The directive keeps it in step with what the user drags. */
    @Input('sortablejs') items: unknown[] | null | undefined;

    @Input('sortablejsOptions') options: Options = {};

    private sortable?: Sortable;

    constructor(private readonly element: ElementRef<HTMLElement>, private readonly zone: NgZone) {}

    ngAfterViewInit(): void {
        // Sortable attaches its own drag listeners; keeping them outside Angular avoids a change
        // detection pass for every mousemove during a drag.
        this.zone.runOutsideAngular(() => {
            this.sortable = Sortable.create(this.element.nativeElement, {
                ...this.options,
                onUpdate: (event: SortableEvent) => {
                    this.moveItem(event.oldIndex, event.newIndex);
                    // The host component's own handler still runs, and still inside Angular so that
                    // anything it changes is picked up.
                    if (this.options.onUpdate) {
                        this.zone.run(() => this.options.onUpdate!(event));
                    }
                },
            });
        });
    }

    ngOnDestroy(): void {
        this.sortable?.destroy();
    }

    private moveItem(from: number | undefined, to: number | undefined): void {
        if (!this.items || from === undefined || to === undefined || from === to) {
            return;
        }
        const [moved] = this.items.splice(from, 1);
        this.items.splice(to, 0, moved);
    }
}
